import React, { useState } from 'react';

const BUTTON_POSITIONS = {
  '1':         { left: '14.20%', top: '11.59%', width: '17.83%', height: '14.61%' },
  '2':         { left: '41.35%', top: '11.59%', width: '17.75%', height: '14.61%' },
  '3':         { left: '68.23%', top: '11.59%', width: '17.92%', height: '14.61%' },
  '4':         { left: '14.20%', top: '32.41%', width: '17.83%', height: '14.61%' },
  '5':         { left: '41.26%', top: '32.33%', width: '17.83%', height: '14.69%' },
  '6':         { left: '68.23%', top: '32.33%', width: '17.83%', height: '14.78%' },
  '7':         { left: '14.20%', top: '53.22%', width: '17.83%', height: '14.45%' },
  '8':         { left: '41.26%', top: '53.22%', width: '17.83%', height: '14.45%' },
  '9':         { left: '68.23%', top: '53.22%', width: '17.92%', height: '14.45%' },
  'clear':     { left: '14.02%', top: '74.04%', width: '18.01%', height: '14.45%' },
  '0':         { left: '41.26%', top: '73.96%', width: '17.83%', height: '14.53%' },
  'backspace': { left: '68.23%', top: '73.88%', width: '17.92%', height: '14.69%' },
};

const KEYPAD_LAYOUT = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['clear', '0', 'backspace'],
];

const FLAT_LAYOUT = KEYPAD_LAYOUT.flat();

function GameKeypad({ onDigit, onBackspace, onClear, disabledButtons = [] }) {
  const [failedImages, setFailedImages] = useState(new Set());
  const [hoveredKey, setHoveredKey] = useState(null);
  const [pressedKey, setPressedKey] = useState(null);

  const getImageSrc = (key) => {
    if (key === 'clear') return '/images/keypad/clear.png';
    if (key === 'backspace') return '/images/keypad/backspace.png';
    return `/images/keypad/${key}.png`;
  };

  const getLabel = (key) => {
    if (key === 'clear') return 'C';
    if (key === 'backspace') return '←';
    return key;
  };

  const handleClick = (key) => {
    if (disabledButtons.includes(key)) return;
    if (key === 'clear') {
      onClear();
    } else if (key === 'backspace') {
      onBackspace();
    } else {
      onDigit(key);
    }
  };

  const handleImageError = (key) => {
    setFailedImages((prev) => new Set(prev).add(key));
  };

  const getFilter = (key, isDisabled) => {
    if (isDisabled) return 'brightness(0.4) grayscale(1)';
    if (pressedKey === key) return 'brightness(1.8)';
    if (hoveredKey === key) return 'brightness(1.4)';
    return '';
  };

  return (
    <div className="relative w-full select-none">
      {/* Base keypad image — sets container size */}
      <img
        src="/images/keypad/keypad.png"
        alt=""
        className="w-full h-auto relative z-0"
        draggable={false}
      />

      {/* Button image layers — all full-size, stacked exactly on top */}
      {FLAT_LAYOUT.map((key) => {
        const isDisabled = disabledButtons.includes(key);
        if (failedImages.has(key)) return null;

        return (
          <img
            key={`layer-${key}`}
            src={getImageSrc(key)}
            alt=""
            className="absolute inset-0 w-full h-full pointer-events-none z-10"
            style={{
              filter: getFilter(key, isDisabled),
              transition: 'filter 0.15s ease',
            }}
            draggable={false}
            onError={() => handleImageError(key)}
          />
        );
      })}

      {/* Clickable bounding boxes mapped to each button's position */}
      {FLAT_LAYOUT.map((key) => {
        const isDisabled = disabledButtons.includes(key);
        const pos = BUTTON_POSITIONS[key];
        const imgFailed = failedImages.has(key);

        return (
          <button
            key={`hit-${key}`}
            style={{
              position: 'absolute',
              left: pos.left,
              top: pos.top,
              width: pos.width,
              height: pos.height,
              zIndex: 20,
              background: 'transparent',
              border: 'none',
              padding: 0,
              margin: 0,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
            }}
            disabled={isDisabled}
            onClick={() => handleClick(key)}
            onMouseEnter={() => setHoveredKey(key)}
            onMouseLeave={() => {
              setHoveredKey(null);
              setPressedKey(null);
            }}
            onMouseDown={() => setPressedKey(key)}
            onMouseUp={() => setPressedKey(null)}
            onTouchStart={() => setPressedKey(key)}
            onTouchEnd={() => setPressedKey(null)}
            aria-label={
              key === 'clear' ? 'Clear' : key === 'backspace' ? 'Backspace' : `Digit ${key}`
            }
          >
            {imgFailed && (
              <div
                className="w-full h-full flex items-center justify-center"
                style={{
                  filter: getFilter(key, isDisabled),
                  transition: 'filter 0.15s ease',
                }}
              >
                <span className="text-hash-gold text-xl sm:text-2xl lg:text-3xl font-bold drop-shadow-lg">
                  {getLabel(key)}
                </span>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

export default GameKeypad;
